import { debugLog, RELAXATION_LEVELS, MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import { generateCombinations, generateUnitCombinations, generatePoolCombinations, generateTeamSplits, generateTeamSplitsRespectingPartnerships } from './combinationGenerator.js';
import { getSinglesPairingPolicy, MATCH_FORMAT_SINGLES } from './competitiveMatchFormat.js';
import {
  DEFAULT_RELAXATION_CONFIG,
  getHardBlocksForLevel,
  getMaxRelaxationLevel,
  getRelaxationMessage,
  getPreConsecutiveMaxRelaxationLevel,
  getConsecutiveMinRelaxationLevel,
} from './normalizedRelaxation.js';
import { DEFAULT_SCORING_CONFIG_SINGLES } from './normalizedScoring.js';
import { pairAllowedByPolicy } from './singlesCourtEngine.js';
import { scoreCombination, scoreCombinationWithFeatureFlags, resetBlockLogCounters } from './scoreEngine.js';
import {
  expandUnitsToPlayers,
  getTotalPlayerCount,
  getUnitPlayers,
  buildUnitsFromPlayers,
} from './unitBuilder.js';
import { filterUnitsForGameTypeHint } from './gameTypeHintUnits.js';
import { isSessionMensDoublesEloExemptLineup } from './competitiveGender.js';
import { isScoringFirstMatchmakingEnabled } from './scoringFirstMatchmaking.js';

/**
 * Returns true when the proposed split matches the game type required by the hint.
 *   'womens' — all 4 players are female
 *   'mens'   — all 4 physically male, or host-endorsed session mens (3M + 1 eligible F)
 *   'mixed'  — each team has at least one male and one female
 */
function splitMatchesGameTypeHint(split, playersById, hint, playerWaitMetrics = {}) {
  const allIds = [...split.team1, ...split.team2];
  const genders = allIds.map(id => (playersById[id]?.gender || '').toLowerCase());
  if (hint === 'womens') return genders.every(g => g === 'female');
  if (hint === 'mens') {
    if (genders.every(g => g === 'male')) return true;
    return isSessionMensDoublesEloExemptLineup(split.team1, split.team2, playerWaitMetrics, playersById);
  }
  if (hint === 'mixed') {
    const t1g = split.team1.map(id => (playersById[id]?.gender || '').toLowerCase());
    const t2g = split.team2.map(id => (playersById[id]?.gender || '').toLowerCase());
    return t1g.includes('male') && t1g.includes('female') &&
           t2g.includes('male') && t2g.includes('female');
  }
  return true;
}

function createScheduledCombinations(scheduledGames = []) {
  // Ensure scheduledGames is an array before calling .map()
  if (!scheduledGames || !Array.isArray(scheduledGames)) {
    return [];
  }
  return scheduledGames.map(game => [
    game.team1_player1_id,
    game.team1_player2_id,
    game.team2_player1_id,
    game.team2_player2_id,
  ]);
}

/**
 * Evaluate best combination from unit combinations
 * @param {Object[][]} unitCombinations - Array of unit combinations (each combination is an array of units)
 * @param {Object} playersById - Map of player objects by ID
 * @param {Array} partnerships - Array of partnership objects
 * @param {Object} sessionState - Session state
 * @param {number} relaxationLevel - Relaxation level
 * @param {Array} scheduledCombinations - Array of scheduled game combinations (player IDs)
 * @param {string} gameTypeRestriction - Game type restriction
 * @param {Object} playerCategories - Player categories
 * @param {number|null} gamesMin - Minimum games
 * @param {Array} scheduledGames - Scheduled games
 * @param {Set<string>|null} playersPassedCooldown - Players who passed cooldown
 * @param {number|null} expandedThreshold - Expanded threshold
 * @param {Object|null} hardBlocks - Hard block overrides from relaxation system
 * @param {boolean} cooldownEnabled - Session-level cooldown setting
 * @returns {Object|null} Best combination result
 */
function evaluateBestCombination(
  unitCombinations, 
  playersById, 
  partnerships, 
  sessionState, 
  relaxationLevel = RELAXATION_LEVELS.STRICT, 
  scheduledCombinations = [], 
  gameTypeRestriction = 'all_types', 
  playerCategories = null, 
  gamesMin = null, 
  scheduledGames = [], 
  playersPassedCooldown = null, 
  expandedThreshold = null,
  hardBlocks = null,
  cooldownEnabled = true,
  courtGameTypeHint = null,
  poolPlayerIds = null,
) {
  // Reset block log counters to avoid console spam
  resetBlockLogCounters();
  
  debugLog('🔎 evaluateBestCombination: Evaluating', unitCombinations.length, 'unit combinations with gamesMin:', gamesMin);
  
  // Log pool composition for debugging
  if (unitCombinations.length > 0) {
    const allPlayerIds = [...new Set(unitCombinations.flatMap(unitCombo => expandUnitsToPlayers(unitCombo)))];
    const males = allPlayerIds.filter(id => playersById[id]?.gender === 'male').length;
    const females = allPlayerIds.filter(id => playersById[id]?.gender === 'female').length;
    debugLog('📊 Pool composition:', {
      totalPlayers: allPlayerIds.length,
      males,
      females,
      gameTypeRestriction,
      partnerships: partnerships?.length || 0,
      unitCombinations: unitCombinations.length,
    });
  }
  
  let bestSplit = null;
  let bestScore = -Infinity;
  if (unitCombinations.length === 0) {
    console.warn('⚠️ evaluateBestCombination: No combinations provided!');
    return null;
  }

  // Accumulate valid scores during the single pass to build topCombinations without a second loop.
  const allScoredCombinations = [];

  // Process unit combinations: expand to player IDs, then generate team splits
  unitCombinations.forEach(unitCombo => {
    // Expand units to player IDs
    const fourPlayerIds = expandUnitsToPlayers(unitCombo);
    if (fourPlayerIds.length !== 4) {
      console.warn(`⚠️ Unit combination expanded to ${fourPlayerIds.length} players (expected 4)`);
      return;
    }
    
    // Generate team splits respecting partnerships
    const splits = generateTeamSplitsRespectingPartnerships(fourPlayerIds, partnerships);
    splits.forEach(split => {
      // When a game-type hint is active, skip splits that don't match it.
      if (courtGameTypeHint && !splitMatchesGameTypeHint(split, playersById, courtGameTypeHint, sessionState?.playerWaitMetrics || {})) return;

      const score = scoreCombinationWithFeatureFlags(
        split,
        playersById,
        partnerships,
        sessionState,
        relaxationLevel,
        scheduledCombinations,
        gameTypeRestriction,
        playerCategories,
        gamesMin,
        scheduledGames,
        playersPassedCooldown,
        expandedThreshold,
        hardBlocks,
        cooldownEnabled,
        null,
        poolPlayerIds,
      );
      if (score > bestScore) {
        bestScore = score;
        bestSplit = split;
      }
      if (score !== -Infinity) {
        allScoredCombinations.push({
          team1: split.team1,
          team2: split.team2,
          team1Names: split.team1.map(id => playersById[id]?.name || id),
          team2Names: split.team2.map(id => playersById[id]?.name || id),
          score,
        });
      }
    });
  });

  // Also process scheduled combinations (they're already player IDs)
  scheduledCombinations.forEach(fourPlayerIds => {
    const splits = generateTeamSplitsRespectingPartnerships(fourPlayerIds, partnerships);
    splits.forEach(split => {
      const score = scoreCombinationWithFeatureFlags(
        split,
        playersById,
        partnerships,
        sessionState,
        relaxationLevel,
        scheduledCombinations,
        gameTypeRestriction,
        playerCategories,
        gamesMin,
        scheduledGames,
        playersPassedCooldown,
        expandedThreshold,
        hardBlocks,
        cooldownEnabled,
        null,
        poolPlayerIds,
      );
      if (score > bestScore) {
        bestScore = score;
        bestSplit = split;
      }
    });
  });

  // Build top combinations from scores already collected — no second scoring pass needed.
  allScoredCombinations.sort((a, b) => b.score - a.score);
  const topCombinations = allScoredCombinations.slice(0, 3);

  if (!bestSplit || bestScore === -Infinity) {
    console.warn('❌ No valid combination found - all combinations scored -Infinity. Best score:', bestScore);
    return null;
  }

  return {
    split: bestSplit,
    score: bestScore,
    topCombinations,
  };
}

const DOUBLES_PLAYERS_PER_MATCH = 4;

/**
 * Enumerate all legal unit combos from the full eligible pool (scoring-first path).
 */
function generateAllPoolCombinations(poolUnits, playersPerMatch = DOUBLES_PLAYERS_PER_MATCH) {
  return generatePoolCombinations([], poolUnits, 0, playersPerMatch);
}

function isUnitAtMinGames(unit, playersById, minGames) {
  if (minGames === null || minGames === undefined) return false;
  const unitPlayers = getUnitPlayers(unit);
  if (unitPlayers.length === 0) return false;
  return unitPlayers.every((playerId) => {
    const player = playersById[playerId];
    if (!player) return false;
    const games = player.effectiveGamesPlayed ?? player.gamesPlayedThisSession ?? 0;
    return games === minGames;
  });
}

function countMandatoryMinGamePlayers(corePool, playersById, minGames, playersPerMatch) {
  const ppm = Math.max(2, playersPerMatch);
  const minUnits = corePool.filter((unit) => isUnitAtMinGames(unit, playersById, minGames));
  return Math.min(ppm, getTotalPlayerCount(minUnits));
}

function poolExpandedBeyondMin(poolResult) {
  if (!poolResult) return false;
  if (poolResult.thresholdsUsed != null && poolResult.thresholdsUsed > 1) return true;
  const { minGames, expandedThreshold } = poolResult;
  return minGames != null && expandedThreshold != null && expandedThreshold > minGames;
}

/**
 * Build ordered core-count plans. When the pool expanded beyond min games, enforce
 * session-min core players without relaxing them out on deadlock.
 */
function buildCoreCountFloorPlans(corePool, flexiblePool, options = {}) {
  const {
    playersPerMatch = DOUBLES_PLAYERS_PER_MATCH,
    poolResult = null,
    playersById = null,
  } = options;

  const ppm = Math.max(2, playersPerMatch);
  const corePlayerCount = getTotalPlayerCount(corePool);
  const flexiblePlayerCount = getTotalPlayerCount(flexiblePool);
  const maxFromCore = Math.min(ppm, corePlayerCount);
  const baseMinFromCore = Math.max(0, ppm - flexiblePlayerCount);

  const toCounts = (floor) => {
    const effectiveFloor = Math.min(Math.max(floor, 0), maxFromCore);
    const counts = [];
    for (let count = maxFromCore; count >= effectiveFloor; count -= 1) {
      counts.push(count);
    }
    if (counts.length === 0) {
      counts.push(0);
    }
    return counts;
  };

  let mandatoryMin = 0;
  if (poolResult && playersById && poolExpandedBeyondMin(poolResult)) {
    mandatoryMin = countMandatoryMinGamePlayers(
      corePool,
      playersById,
      poolResult.minGames,
      ppm,
    );
  }

  const guaranteedWaitPlayerCount = poolResult?.guaranteedByWait?.length
    ? getTotalPlayerCount(poolResult.guaranteedByWait)
    : 0;
  const guaranteedWaitFloor = Math.min(ppm, guaranteedWaitPlayerCount);

  const preferredFloor = Math.max(baseMinFromCore, mandatoryMin, guaranteedWaitFloor);
  const plans = [];

  if (mandatoryMin > 0) {
    // Session-min players must stay in the match when the pool expanded past them.
    // Do not relax inclusion and drop a lone G:0 for an all-G:2 quartet.
    plans.push({
      counts: toCounts(preferredFloor),
      mandatoryMinIncluded: true,
      relaxedFrom: null,
      relaxedTo: null,
    });
  } else {
    plans.push({
      counts: toCounts(preferredFloor),
      mandatoryMinIncluded: false,
      relaxedFrom: null,
      relaxedTo: null,
    });
  }

  return plans;
}

function appendMinInclusionRelaxationDetail(relaxationDetails, plan) {
  if (
    plan.mandatoryMinIncluded === false
    && plan.relaxedFrom != null
    && plan.relaxedTo != null
  ) {
    relaxationDetails.push(
      `Min-game inclusion relaxed (preferred ${plan.relaxedFrom} core players, used ${plan.relaxedTo})`,
    );
  }
}

/**
 * Core/flexible player counts for a game of size playersPerMatch (e.g. 2 singles).
 */
function buildCoreCountsForMatch(corePool, flexiblePool, playersPerMatch, options = {}) {
  return buildCoreCountFloorPlans(corePool, flexiblePool, {
    ...options,
    playersPerMatch,
  });
}

/**
 * Best 1v1 pair from unit combinations (normalized scoring + singles weights).
 */
function evaluateBestSinglesCombination(
  unitCombinations,
  playersById,
  partnerships,
  sessionState,
  relaxationLevel = RELAXATION_LEVELS.STRICT,
  scheduledCombinations = [],
  gameTypeRestriction = 'all_types',
  playerCategories = null,
  gamesMin = null,
  scheduledGames = [],
  playersPassedCooldown = null,
  expandedThreshold = null,
  hardBlocks = null,
  cooldownEnabled = true
) {
  resetBlockLogCounters();

  debugLog('🔎 evaluateBestSinglesCombination:', unitCombinations.length, 'combos, gamesMin:', gamesMin);

  let bestSplit = null;
  let bestScore = -Infinity;
  if (!unitCombinations || unitCombinations.length === 0) {
    console.warn('⚠️ evaluateBestSinglesCombination: No combinations provided!');
    return null;
  }

  const policy = getSinglesPairingPolicy(gameTypeRestriction, MATCH_FORMAT_SINGLES);

  const scoreSplit = (split) => {
    const [a, b] = [split.team1[0], split.team2[0]];
    const p1 = playersById[a];
    const p2 = playersById[b];
    if (!p1 || !p2) return -Infinity;
    if (!pairAllowedByPolicy(p1.gender, p2.gender, policy)) return -Infinity;
    return scoreCombinationWithFeatureFlags(
      split,
      playersById,
      partnerships,
      sessionState,
      relaxationLevel,
      scheduledCombinations,
      gameTypeRestriction,
      playerCategories,
      gamesMin,
      scheduledGames,
      playersPassedCooldown,
      expandedThreshold,
      hardBlocks,
      cooldownEnabled,
      DEFAULT_SCORING_CONFIG_SINGLES
    );
  };

  unitCombinations.forEach((unitCombo) => {
    const twoPlayerIds = expandUnitsToPlayers(unitCombo);
    if (twoPlayerIds.length !== 2) {
      console.warn(`⚠️ Singles unit combo expanded to ${twoPlayerIds.length} players (expected 2)`);
      return;
    }
    const split = { team1: [twoPlayerIds[0]], team2: [twoPlayerIds[1]] };
    const score = scoreSplit(split);
    if (score > bestScore) {
      bestScore = score;
      bestSplit = split;
    }
  });

  scheduledCombinations.forEach((row) => {
    const ids = (row || []).filter(Boolean);
    if (ids.length !== 2) return;
    const split = { team1: [ids[0]], team2: [ids[1]] };
    const score = scoreSplit(split);
    if (score > bestScore) {
      bestScore = score;
      bestSplit = split;
    }
  });

  const allCombinations = [];
  unitCombinations.forEach((unitCombo) => {
    const twoPlayerIds = expandUnitsToPlayers(unitCombo);
    if (twoPlayerIds.length !== 2) return;
    const split = { team1: [twoPlayerIds[0]], team2: [twoPlayerIds[1]] };
    const score = scoreSplit(split);
    if (score !== -Infinity) {
      allCombinations.push({
        team1: split.team1,
        team2: split.team2,
        team1Names: split.team1.map(id => playersById[id]?.name || id),
        team2Names: split.team2.map(id => playersById[id]?.name || id),
        score,
      });
    }
  });

  allCombinations.sort((a, b) => b.score - a.score);
  const topCombinations = allCombinations.slice(0, 3);

  if (!bestSplit || bestScore === -Infinity) {
    debugLog('❌ No valid singles combination; best score:', bestScore);
    return null;
  }

  return {
    split: bestSplit,
    score: bestScore,
    topCombinations,
  };
}

/**
 * Prioritize flexible pool units based on greedy selected player IDs
 * @param {Object[]} flexibleUnits - Array of flexible units
 * @param {string[]} greedyPlayerIds - Array of greedy selected player IDs
 * @returns {Object[]} Prioritized array of units
 */
function prioritizeFlexiblePool(flexibleUnits = [], greedyPlayerIds = []) {
  if (!greedyPlayerIds || greedyPlayerIds.length === 0) {
    return flexibleUnits.slice();
  }
  const greedySet = new Set(greedyPlayerIds);
  const prioritized = [];
  const used = new Set();
  
  // First, add units that contain any greedy selected players
  flexibleUnits.forEach(unit => {
    const unitPlayers = getUnitPlayers(unit);
    if (unitPlayers.some(id => greedySet.has(id))) {
      prioritized.push(unit);
      used.add(unit.id);
    }
  });
  
  // Then, add remaining units
  flexibleUnits.forEach(unit => {
    if (!used.has(unit.id)) {
      prioritized.push(unit);
    }
  });
  
  return prioritized;
}

/**
 * Build core and flexible pools from pool result (works with units)
 * @param {Object} poolResult - Pool build result with units
 * @param {string[]} greedyPlayerIds - Array of greedy selected player IDs
 * @returns {Object} Object with corePool and flexiblePool (both arrays of units)
 */
function buildCoreAndFlexiblePools(poolResult, greedyPlayerIds = []) {
  const guaranteedUnits = poolResult.guaranteedPlayers || [];
  const flexibleUnits = poolResult.flexiblePlayers || [];

  // When variety-aware mode is on, the pool is already ranked by marginalVarietyGain.
  // Skip the fallback greedy→core promotion so no units are force-locked into every
  // combo evaluation — let the full shaped pool compete fairly in the flexible slot.
  // Core = only genuinely guaranteed units (wait-guarantee + partnership reunions).
  if (poolResult.varietyAwareMode) {
    // Build flexible from the full shaped pool (preserving shaping order), minus guaranteed units
    const guaranteedIds = new Set(guaranteedUnits.flatMap(u => getUnitPlayers(u)));
    const allPoolUnits = poolResult.pool || [];
    const flexibleFromShapedPool = allPoolUnits.filter(
      u => !getUnitPlayers(u).every(id => guaranteedIds.has(id)),
    );
    return {
      corePool: guaranteedUnits,
      flexiblePool: flexibleFromShapedPool,
    };
  }

  const prioritizedFlexible = prioritizeFlexiblePool(flexibleUnits, greedyPlayerIds);
  
  if (guaranteedUnits.length === 0 && greedyPlayerIds.length > 0) {
    const greedySet = new Set(greedyPlayerIds);
    // Filter units that contain any greedy selected players
    const fallbackCore = prioritizedFlexible.filter(unit => {
      const unitPlayers = getUnitPlayers(unit);
      return unitPlayers.some(id => greedySet.has(id));
    });
    const fallbackCoreIds = new Set(fallbackCore.map(u => u.id));
    const flexibleRemainder = prioritizedFlexible.filter(unit => !fallbackCoreIds.has(unit.id));
    return {
      corePool: fallbackCore,
      flexiblePool: flexibleRemainder,
    };
  }
  return {
    corePool: guaranteedUnits,
    flexiblePool: prioritizedFlexible,
  };
}


/**
 * Run game type fallback logic when strict matching fails
 * Refactored to work with units throughout to preserve partnerships
 * @param {Object[]} availablePlayers - Available player IDs (for calculating unique games)
 * @param {Object} poolResult - Pool result with units
 * @param {Object} playersById - Map of player objects
 * @param {Array} partnerships - Array of partnerships
 * @param {Object} sessionState - Session state
 * @param {Array} scheduledCombinations - Scheduled combinations
 * @param {Array} scheduledGames - Scheduled games
 * @param {string} gameTypeRestriction - Game type restriction
 * @param {Object} playerCategories - Player categories
 * @returns {Object|null} Result with split or null
 */
function runGameTypeFallback(availablePlayers, poolResult, playersById, partnerships, sessionState, scheduledCombinations, scheduledGames, gameTypeRestriction, playerCategories) {
  const gamesMin = poolResult.minGames;
  
  // Work with units from poolResult.pool (preserves partnerships)
  const availableUnits = poolResult.pool || [];
  
  if (availableUnits.length === 0) {
    return null;
  }
  
  // Calculate unique games played from available players (for threshold calculation)
  const uniqueGamesPlayed = new Set();
  availablePlayers.forEach(playerId => {
    const player = playersById[playerId];
    if (player) {
      const effectiveGames = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
      uniqueGamesPlayed.add(effectiveGames);
    }
  });
  const sortedUniqueGames = Array.from(uniqueGamesPlayed).sort((a, b) => a - b);
  const minGamesIndex = sortedUniqueGames.indexOf(gamesMin);
  if (minGamesIndex === -1) {
    return null;
  }
  const nextTierIndex = minGamesIndex < sortedUniqueGames.length - 1 ? minGamesIndex + 1 : minGamesIndex;
  const nextTierThreshold = sortedUniqueGames[nextTierIndex] || gamesMin + 1;
  
  /**
   * Check if all players in a unit are at minimum games
   */
  const isUnitAtMin = (unit) => {
    const unitPlayers = getUnitPlayers(unit);
    return unitPlayers.every(playerId => {
    const player = playersById[playerId];
    if (!player) return false;
    const effectiveGames = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
      return effectiveGames === gamesMin;
    });
  };
  
  /**
   * Check if a unit is in the next tier (all players <= threshold, at least one > min)
   */
  const isUnitInNextTier = (unit) => {
    const unitPlayers = getUnitPlayers(unit);
    let allWithinThreshold = true;
    let hasPlayerAboveMin = false;
    
    unitPlayers.forEach(playerId => {
      const player = playersById[playerId];
      if (!player) {
        allWithinThreshold = false;
        return;
      }
    const effectiveGames = player.effectiveGamesPlayed || player.gamesPlayedThisSession || 0;
      if (effectiveGames > nextTierThreshold) {
        allWithinThreshold = false;
      }
      if (effectiveGames > gamesMin) {
        hasPlayerAboveMin = true;
      }
    });
    
    return allWithinThreshold && hasPlayerAboveMin;
  };
  
  // Filter units by tier
  const unitsAtMin = availableUnits.filter(unit => isUnitAtMin(unit));
  const unitsInNextTier = availableUnits.filter(unit => isUnitInNextTier(unit));
  
  if (unitsAtMin.length === 0) {
    return null;
  }
  
  // Calculate player counts from units
  const playersAtMinCount = getTotalPlayerCount(unitsAtMin);
  const playersInNextTierCount = getTotalPlayerCount(unitsInNextTier);
  
  // Try different combinations of players from min tier and next tier
  for (let minPlayersCount = Math.min(3, playersAtMinCount); minPlayersCount >= 0; minPlayersCount -= 1) {
    const slotsNeeded = 4 - minPlayersCount;
    
    if (playersInNextTierCount < slotsNeeded) {
      continue;
    }
    
    // Generate unit combinations that add up to the target player counts
    // Use generateUnitCombinations which respects unit integrity (partnerships stay together)
    const unitCombinations = [];
    
    if (minPlayersCount === 0) {
      // All 4 players from next tier
      const nextTierCombos = generateUnitCombinations(unitsInNextTier, 4);
      unitCombinations.push(...nextTierCombos);
    } else if (minPlayersCount === 4) {
      // All 4 players from min tier
      const minTierCombos = generateUnitCombinations(unitsAtMin, 4);
      unitCombinations.push(...minTierCombos);
    } else {
      // Mix: minPlayersCount from min tier, slotsNeeded from next tier
      const minTierCombos = generateUnitCombinations(unitsAtMin, minPlayersCount);
      const nextTierCombos = generateUnitCombinations(unitsInNextTier, slotsNeeded);
      
      // Combine unit combinations from both tiers
      minTierCombos.forEach(minCombo => {
        nextTierCombos.forEach(nextCombo => {
          const merged = [...minCombo, ...nextCombo];
          const totalPlayers = getTotalPlayerCount(merged);
          if (totalPlayers === 4) {
            unitCombinations.push(merged);
          }
        });
      });
    }
    
    if (unitCombinations.length === 0) {
      continue;
    }
    
    // Evaluate the unit combinations
    const expandedResult = evaluateBestCombination(
      unitCombinations,
      playersById,
      partnerships,
      sessionState,
      RELAXATION_LEVELS.STRICT,
      scheduledCombinations,
      gameTypeRestriction,
      playerCategories,
      gamesMin,
      scheduledGames,
      poolResult.playersPassedCooldown,
      nextTierThreshold,
    );
    
    if (expandedResult) {
      const relaxationDetails = [`Game type restriction required expanding pool (${minPlayersCount} of 4 players from bottom threshold)`];
      return {
        result: expandedResult.split || expandedResult,
        relaxationLevel: RELAXATION_LEVELS.STRICT,
        relaxationDetails,
        topCombinations: expandedResult.topCombinations || [],
      };
    }
  }
  
  return null;
}

function evaluateWithProgressiveRelaxation(poolResult, playersById, partnerships, sessionState, greedyPlayerIds = [], scheduledGames = [], gameTypeRestriction = 'all_types', playerCategories = null, allAvailablePlayers = null) {
  const poolUnits = poolResult?.pool;
  if (!poolResult || !Array.isArray(poolUnits) || getTotalPlayerCount(poolUnits) < 4) {
    return { result: null };
  }

  const availablePlayers = allAvailablePlayers || poolResult.pool;
  const scheduledCombinations = createScheduledCombinations(scheduledGames);
  const relaxationOrder = [
    RELAXATION_LEVELS.STRICT,
    RELAXATION_LEVELS.ALLOW_REPEAT_TEAMS,
  ];

  const { corePool, flexiblePool } = buildCoreAndFlexiblePools(poolResult, greedyPlayerIds);
  const coreCountPlans = buildCoreCountFloorPlans(corePool, flexiblePool, {
    poolResult,
    playersById,
  });

  for (const plan of coreCountPlans) {
    for (const level of relaxationOrder) {
      for (const coreCount of plan.counts) {
        const unitCombinations = generatePoolCombinations(corePool, flexiblePool, coreCount);

        if (unitCombinations.length === 0) {
          continue;
        }

        const result = evaluateBestCombination(
          unitCombinations,
          playersById,
          partnerships,
          sessionState,
          level,
          scheduledCombinations,
          gameTypeRestriction,
          playerCategories,
          poolResult.minGames,
          scheduledGames,
          poolResult.playersPassedCooldown,
          poolResult.expandedThreshold,
        );
        if (result) {
          const relaxationDetails = [];
          appendMinInclusionRelaxationDetail(relaxationDetails, plan);
          if (level > RELAXATION_LEVELS.STRICT) {
            const levelNames = {
              [RELAXATION_LEVELS.ALLOW_REPEAT_TEAMS]: 'Repeat teams allowed',
            };
            relaxationDetails.push(levelNames[level]);
          }
          return {
            result: result.split || result,
            relaxationLevel: level,
            relaxationDetails,
            topCombinations: result.topCombinations || [],
          };
        }
      }

      if (level === RELAXATION_LEVELS.STRICT && gameTypeRestriction && gameTypeRestriction !== 'all_types') {
        const fallbackResult = runGameTypeFallback(
          availablePlayers,
          poolResult,
          playersById,
          partnerships,
          sessionState,
          scheduledCombinations,
          scheduledGames,
          gameTypeRestriction,
          playerCategories,
        );
        if (fallbackResult) {
          appendMinInclusionRelaxationDetail(fallbackResult.relaxationDetails || [], plan);
          return fallbackResult;
        }
      }
    }
  }

  debugLog('🚨 All relaxation levels exhausted - no valid combination found');
  return { result: null };
}

/**
 * Try game-type-hinted unit combinations at one relaxation level.
 */
function tryHintedCombinationsAtLevel({
  corePool,
  flexiblePool,
  coreCounts,
  playersById,
  partnerships,
  sessionState,
  level,
  scheduledCombinations,
  gameTypeRestriction,
  playerCategories,
  poolResult,
  scheduledGames,
  hardBlocks,
  cooldownEnabled,
  courtGameTypeHint,
  poolPlayerIds,
}) {
  for (const coreCount of coreCounts) {
    const unitCombinations = generatePoolCombinations(corePool, flexiblePool, coreCount);
    if (unitCombinations.length === 0) continue;

    const hintedResult = evaluateBestCombination(
      unitCombinations,
      playersById,
      partnerships,
      sessionState,
      level,
      scheduledCombinations,
      gameTypeRestriction,
      playerCategories,
      poolResult.minGames,
      scheduledGames,
      poolResult.playersPassedCooldown,
      poolResult.expandedThreshold,
      hardBlocks,
      cooldownEnabled,
      courtGameTypeHint,
      poolPlayerIds,
    );

    if (hintedResult) {
      return hintedResult;
    }
  }

  return null;
}

/**
 * Probe whether any strict-level combo exists with quartet novelty (session debt).
 */
function runNormalizedRelaxationPass({
  corePool,
  flexiblePool,
  poolResult,
  playersById,
  partnerships,
  sessionState,
  scheduledCombinations,
  scheduledGames,
  gameTypeRestriction,
  playerCategories,
  availablePlayers,
  config,
  levelMin,
  levelMax,
  cooldownEnabled,
  courtGameTypeHint,
  poolPlayerIds = null,
  coreCountPlans = null,
}) {
  const scoringFirst = isScoringFirstMatchmakingEnabled() || poolResult?.scoringFirstMode === true;
  const fullPoolUnits = poolResult?.pool || [...(corePool || []), ...(flexiblePool || [])];
  const plans = scoringFirst
    ? [{ counts: [0], mandatoryMinIncluded: false, relaxedFrom: null, relaxedTo: null }]
    : (coreCountPlans || buildCoreCountFloorPlans(corePool, flexiblePool, {
      poolResult,
      playersById,
    }));
  const priorityMaxLevel = MATCHMAKING_CONFIG.gameTypeBalance?.priorityRelaxationMaxLevel ?? 5;

  for (const plan of plans) {
    const coreCounts = plan.counts;

    for (let level = levelMin; level <= levelMax; level++) {
      const hardBlocks = getHardBlocksForLevel(config, level, cooldownEnabled);

      debugLog(`🔄 Trying relaxation level ${level}/${levelMax}`, {
        levelName: hardBlocks.relaxationName || 'Strict',
        courtGameTypeHint,
        hardBlocks: {
          categoryGap: hardBlocks.categoryGap,
          consecutiveThreshold: hardBlocks.consecutiveThreshold,
          enforceSessionInteractionDensity: hardBlocks.enforceSessionInteractionDensity,
          allowExactRepeat: hardBlocks.allowExactRepeat,
          allowImmediateRepeat: hardBlocks.allowImmediateRepeat,
          minPlus2Guard: hardBlocks.minPlus2Guard,
          enforceCooldown: hardBlocks.enforceCooldown,
          enforceGameType: hardBlocks.enforceGameType,
        },
      });

      if (courtGameTypeHint && level <= priorityMaxLevel) {
        const hintedResult = tryHintedCombinationsAtLevel({
          corePool,
          flexiblePool,
          coreCounts,
          playersById,
          partnerships,
          sessionState,
          level,
          scheduledCombinations,
          gameTypeRestriction,
          playerCategories,
          poolResult,
          scheduledGames,
          hardBlocks,
          cooldownEnabled,
          courtGameTypeHint,
          poolPlayerIds,
        });

        if (hintedResult) {
          const relaxationMessage = getRelaxationMessage(level, hardBlocks);
          const relaxationDetails = level > 0
            ? [`${hardBlocks.relaxationName} (game-type priority: ${courtGameTypeHint})`]
            : [];
          appendMinInclusionRelaxationDetail(relaxationDetails, plan);
          debugLog(`✅ Game-type priority hint (${courtGameTypeHint}) honoured at level`, level, {
            score: hintedResult.score,
          });
          return {
            result: hintedResult.split || hintedResult,
            relaxationLevel: level,
            relaxationDetails,
            topCombinations: hintedResult.topCombinations || [],
            hardBlocks,
            relaxationMessage,
          };
        }

        if (level < priorityMaxLevel) {
          debugLog(`ℹ️ Game-type priority (${courtGameTypeHint}): no match at level ${level}, trying next level`);
          continue;
        }
        debugLog(`ℹ️ Game-type priority (${courtGameTypeHint}): no match through level ${priorityMaxLevel}, falling back to normal`);
      }

      for (const coreCount of coreCounts) {
        const unitCombinations = scoringFirst
          ? generateAllPoolCombinations(fullPoolUnits, DOUBLES_PLAYERS_PER_MATCH)
          : generatePoolCombinations(corePool, flexiblePool, coreCount);
        if (unitCombinations.length === 0) continue;

        const result = evaluateBestCombination(
          unitCombinations,
          playersById,
          partnerships,
          sessionState,
          level,
          scheduledCombinations,
          gameTypeRestriction,
          playerCategories,
          poolResult.minGames,
          scheduledGames,
          poolResult.playersPassedCooldown,
          poolResult.expandedThreshold,
          hardBlocks,
          cooldownEnabled,
          null,
          poolPlayerIds,
        );

        if (result) {
          const relaxationMessage = getRelaxationMessage(level, hardBlocks);
          const relaxationDetails = level > 0 ? [hardBlocks.relaxationName] : [];
          appendMinInclusionRelaxationDetail(relaxationDetails, plan);

          debugLog(`✅ Match found at level ${level}`, {
            levelName: hardBlocks.relaxationName,
            score: result.score,
            message: relaxationMessage.message,
          });

          return {
            result: result.split || result,
            relaxationLevel: level,
            relaxationDetails,
            topCombinations: result.topCombinations || [],
            hardBlocks,
            relaxationMessage,
          };
        }
      }

      if (level === 0 && gameTypeRestriction && gameTypeRestriction !== 'all_types') {
        debugLog('🔄 Trying game type fallback at strict level');
        const fallbackResult = runGameTypeFallback(
          availablePlayers,
          poolResult,
          playersById,
          partnerships,
          sessionState,
          scheduledCombinations,
          scheduledGames,
          gameTypeRestriction,
          playerCategories,
        );
        if (fallbackResult) {
          appendMinInclusionRelaxationDetail(fallbackResult.relaxationDetails || [], plan);
          return fallbackResult;
        }
      }
    }
  }

  return { result: null };
}

/**
 * NEW: Evaluate with 8-step progressive relaxation (normalized hard blocks)
 * Uses normalized scoring hard blocks instead of legacy relaxation levels
 * 
 * @param {Object} poolResult - Pool result from buildEligiblePool
 * @param {Object} playersById - Map of player objects
 * @param {Array} partnerships - Partnerships array
 * @param {Object} sessionState - Session state
 * @param {string[]} greedyPlayerIds - Greedy selected player IDs
 * @param {Array} scheduledGames - Scheduled games
 * @param {string} gameTypeRestriction - Game type restriction
 * @param {Object} playerCategories - Player categories
 * @param {string[]} allAvailablePlayers - All available players
 * @param {Object} relaxationConfig - Optional custom relaxation config
 * @returns {Object} Result with match and relaxation details
 */
function evaluateWithNormalizedRelaxation(
  poolResult, 
  playersById, 
  partnerships, 
  sessionState, 
  greedyPlayerIds = [], 
  scheduledGames = [], 
  gameTypeRestriction = 'all_types', 
  playerCategories = null, 
  allAvailablePlayers = null,
  relaxationConfig = null,
  phaseBounds = null,
  courtGameTypeHint = null,
  partnershipPoolPlayerIds = null,
) {
  // Import relaxation config utilities
  
  const poolUnits = poolResult?.pool;
  if (!poolResult || !Array.isArray(poolUnits) || getTotalPlayerCount(poolUnits) < 4) {
    return { result: null };
  }

  const availablePlayers = allAvailablePlayers || poolResult.pool;
  const poolPlayerIds = partnershipPoolPlayerIds
    || (Array.isArray(availablePlayers)
      ? availablePlayers
      : expandUnitsToPlayers(poolUnits));
  const scheduledCombinations = createScheduledCombinations(scheduledGames);
  const config = relaxationConfig || DEFAULT_RELAXATION_CONFIG;
  
  // Cooldown: Always true for now (session-level setting can be added later)
  const cooldownEnabled = sessionState?.cooldownEnabled === true;
  
  const globalMaxLevel = getMaxRelaxationLevel(config);
  const levelMin = phaseBounds?.min ?? 0;
  const levelMax = Math.min(phaseBounds?.max ?? globalMaxLevel, globalMaxLevel);

  debugLog('🎯 Starting normalized progressive relaxation', {
    globalMaxLevel,
    levelMin,
    levelMax,
    poolSize: poolUnits.length,
    poolPlayerCount: getTotalPlayerCount(poolUnits),
    gameTypeRestriction,
    cooldownEnabled,
    scoringFirst: isScoringFirstMatchmakingEnabled() || poolResult?.scoringFirstMode === true,
  });

  const scoringFirst = isScoringFirstMatchmakingEnabled() || poolResult?.scoringFirstMode === true;
  const { corePool, flexiblePool } = scoringFirst
    ? { corePool: [], flexiblePool: poolUnits }
    : buildCoreAndFlexiblePools(poolResult, greedyPlayerIds);

  const passArgs = {
    poolResult,
    playersById,
    partnerships,
    sessionState,
    scheduledCombinations,
    scheduledGames,
    gameTypeRestriction,
    playerCategories,
    availablePlayers,
    config,
    levelMin,
    levelMax,
    cooldownEnabled,
    courtGameTypeHint,
    poolPlayerIds,
  };

  if (courtGameTypeHint) {
    const playerWaitMetrics = sessionState?.playerWaitMetrics || {};
    const safeCore = filterUnitsForGameTypeHint(corePool, playersById, courtGameTypeHint, playerWaitMetrics);
    const safeFlex = filterUnitsForGameTypeHint(flexiblePool, playersById, courtGameTypeHint, playerWaitMetrics);
    if (getTotalPlayerCount([...safeCore, ...safeFlex]) >= 4) {
      debugLog(`🤝 Trying partnership-safe ${courtGameTypeHint} pool first`, {
        safeUnits: safeCore.length + safeFlex.length,
        safePlayers: getTotalPlayerCount([...safeCore, ...safeFlex]),
      });
      const safeResult = runNormalizedRelaxationPass({
        ...passArgs,
        corePool: safeCore,
        flexiblePool: safeFlex,
      });
      if (safeResult.result) {
        return safeResult;
      }
    }

    if (courtGameTypeHint === 'mens' || courtGameTypeHint === 'womens') {
      const mixedCore = filterUnitsForGameTypeHint(corePool, playersById, 'mixed');
      const mixedFlex = filterUnitsForGameTypeHint(flexiblePool, playersById, 'mixed');
      if (getTotalPlayerCount([...mixedCore, ...mixedFlex]) >= 4) {
        debugLog(`🤝 Trying mixed partnership-safe pool before full ${courtGameTypeHint} fallback`);
        const mixedHintResult = runNormalizedRelaxationPass({
          ...passArgs,
          corePool: mixedCore,
          flexiblePool: mixedFlex,
          courtGameTypeHint: 'mixed',
        });
        if (mixedHintResult.result) {
          mixedHintResult.relaxationDetails = [
            ...(mixedHintResult.relaxationDetails || []),
            `Used mixed game instead of ${courtGameTypeHint} priority to keep partnerships together`,
          ];
          return mixedHintResult;
        }

        const mixedOpenResult = runNormalizedRelaxationPass({
          ...passArgs,
          corePool: mixedCore,
          flexiblePool: mixedFlex,
          courtGameTypeHint: null,
        });
        if (mixedOpenResult.result) {
          mixedOpenResult.relaxationDetails = [
            ...(mixedOpenResult.relaxationDetails || []),
            `Used mixed game instead of ${courtGameTypeHint} priority to keep partnerships together`,
          ];
          return mixedOpenResult;
        }
      }
    }
  }

  const fullResult = runNormalizedRelaxationPass({
    ...passArgs,
    corePool,
    flexiblePool,
  });

  if (fullResult.result && courtGameTypeHint) {
    fullResult.relaxationDetails = [
      ...(fullResult.relaxationDetails || []),
      `Deferred ${courtGameTypeHint} game-type priority`,
    ];
  }

  if (!fullResult.result) {
    debugLog('🚨 All normalized relaxation levels exhausted - no valid combination found', {
      levelMin,
      levelMax,
      poolSize: poolUnits.length,
      poolPlayerCount: getTotalPlayerCount(poolUnits),
    });
  }

  return fullResult;
}

/**
 * Run progressive relaxation in three phases:
 *   1. Narrow pool (non-cooldown) at strict only
 *   2. Full waitlist with cooldown expanded — levels 0..pre-consecutive
 *   3. Full waitlist — consecutive3+ only (never on a narrowed subsection)
 *
 * @param {Object} params
 * @param {string[]} params.narrowPlayerIds
 * @param {string[]|null} params.fullWaitlistPlayerIds
 * @param {Function} params.evaluateForPlayers - (playerIds, { forceRelaxCooldown, phaseBounds }) => court eval bundle
 * @returns {Object|null} court eval bundle with relaxationResult.result or null
 */
export function runPoolExpansionRelaxationGate({
  narrowPlayerIds,
  fullWaitlistPlayerIds,
  evaluateForPlayers,
}) {
  const config = DEFAULT_RELAXATION_CONFIG;
  const preConsecutiveMax = getPreConsecutiveMaxRelaxationLevel(config);
  const consecutiveMin = getConsecutiveMinRelaxationLevel(config);
  const globalMax = getMaxRelaxationLevel(config);

  const fullIds = Array.isArray(fullWaitlistPlayerIds) && fullWaitlistPlayerIds.length >= 4
    ? fullWaitlistPlayerIds
    : narrowPlayerIds;
  const hasLargerFullPool = fullIds.length > narrowPlayerIds.length;

  debugLog('[POOL EXPANSION GATE] starting', {
    narrow: narrowPlayerIds.length,
    full: fullIds.length,
    hasLargerFullPool,
    preConsecutiveMax,
    consecutiveMin,
  });

  // Phase 1: narrow subsection, strict only
  if (narrowPlayerIds.length >= 4) {
    const phase1 = evaluateForPlayers(narrowPlayerIds, {
      forceRelaxCooldown: false,
      phaseBounds: { min: 0, max: 0 },
    });
    if (phase1?.relaxationResult?.result) {
      debugLog('[POOL EXPANSION GATE] match at phase 1 (narrow strict)');
      return phase1;
    }
  }

  // Phase 2: full waitlist, cooldown pool expansion, all steps before consecutive3
  if (fullIds.length >= 4 && preConsecutiveMax >= 0) {
    const phase2 = evaluateForPlayers(fullIds, {
      forceRelaxCooldown: true,
      phaseBounds: { min: 0, max: preConsecutiveMax },
    });
    if (phase2?.relaxationResult?.result) {
      debugLog('[POOL EXPANSION GATE] match at phase 2 (full pool, pre-consecutive)');
      return phase2;
    }
  }

  // Phase 3: full waitlist, consecutive interaction + game-type last resort
  if (fullIds.length >= 4 && consecutiveMin <= globalMax) {
    const phase3 = evaluateForPlayers(fullIds, {
      forceRelaxCooldown: true,
      phaseBounds: { min: consecutiveMin, max: globalMax },
    });
    if (phase3?.relaxationResult?.result) {
      debugLog('[POOL EXPANSION GATE] match at phase 3 (full pool, consecutive+)');
      return phase3;
    }
  }

  debugLog('[POOL EXPANSION GATE] exhausted all phases');
  return null;
}

/**
 * Normalized progressive relaxation for singles (2 players per game).
 */
function evaluateWithNormalizedRelaxationSingles(
  poolResult,
  playersById,
  partnerships,
  sessionState,
  greedyPlayerIds = [],
  scheduledGames = [],
  gameTypeRestriction = 'all_types',
  playerCategories = null,
  allAvailablePlayers = null,
  relaxationConfig = null,
) {

  const poolUnits = poolResult?.pool;
  if (!poolResult || !Array.isArray(poolUnits) || getTotalPlayerCount(poolUnits) < 2) {
    return { result: null };
  }

  const scheduledCombinations = createScheduledCombinations(scheduledGames);
  const config = relaxationConfig || DEFAULT_RELAXATION_CONFIG;
  const cooldownEnabled = sessionState?.cooldownEnabled === true;
  const maxLevel = getMaxRelaxationLevel(config);

  debugLog('🎯 Starting normalized progressive relaxation (singles)', {
    maxLevel,
    poolPlayerCount: getTotalPlayerCount(poolUnits),
    gameTypeRestriction,
  });

  const { corePool, flexiblePool } = buildCoreAndFlexiblePools(poolResult, greedyPlayerIds);
  const coreCountPlans = buildCoreCountFloorPlans(corePool, flexiblePool, {
    poolResult,
    playersById,
    playersPerMatch: 2,
  });

  for (const plan of coreCountPlans) {
    const singlesPoolCount = getTotalPlayerCount([...corePool, ...flexiblePool]);
    for (let level = 0; level <= maxLevel; level++) {
      const hardBlocks = getHardBlocksForLevel(config, level, cooldownEnabled);

      debugLog(`🔄 Singles: relaxation level ${level}/${maxLevel}`, {
        levelName: hardBlocks.relaxationName || 'Strict',
      });

      for (const coreCount of plan.counts) {
        const unitCombinations = generatePoolCombinations(corePool, flexiblePool, coreCount, 2);

        if (unitCombinations.length === 0) {
          continue;
        }

        const result = evaluateBestSinglesCombination(
          unitCombinations,
          playersById,
          partnerships,
          sessionState,
          level,
          scheduledCombinations,
          gameTypeRestriction,
          playerCategories,
          poolResult.minGames,
          scheduledGames,
          poolResult.playersPassedCooldown,
          poolResult.expandedThreshold,
          hardBlocks,
          cooldownEnabled
        );

        if (result) {
          const relaxationMessage = getRelaxationMessage(level, hardBlocks);
          const relaxationDetails = level > 0 ? [hardBlocks.relaxationName] : [];
          appendMinInclusionRelaxationDetail(relaxationDetails, plan);

          return {
            result: result.split || result,
            relaxationLevel: level,
            relaxationDetails,
            topCombinations: result.topCombinations || [],
            hardBlocks,
            relaxationMessage,
          };
        }
      }
    }
  }

  debugLog('🚨 Singles: all normalized relaxation levels exhausted', {
    maxLevel,
    poolPlayerCount: getTotalPlayerCount(poolUnits),
  });

  return { result: null };
}

export {
  evaluateBestCombination,
  evaluateBestSinglesCombination,
  evaluateWithProgressiveRelaxation,
  evaluateWithNormalizedRelaxation,
  evaluateWithNormalizedRelaxationSingles,
};

